import { apiClient } from "@apis/apiClient";

const humanize = (s: string) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Derive a clean analytics URL and page title from the current browser location.
 * Strips numeric-only path segments (entity IDs) so pages group together in analytics,
 * and appends the active tab (if any) as a trailing segment.
 */
export function derivePageInfo(): { url: string; title: string } {
  const pathname =
    window.location.pathname
      .split("/")
      .filter((s) => s && !/^\d+$/.test(s))
      .join("/") || "/";
  const cleanPathname = pathname.startsWith("/") ? pathname : `/${pathname}`;

  const params = new URLSearchParams(window.location.search);
  const tabActive = params.get("tabActive");
  const friendlyTab =
    tabActive?.replace(/_tab$/, "").replace(/^asset_/, "") ?? null;

  const url = friendlyTab ? `${cleanPathname}/${friendlyTab}` : cleanPathname;

  const segments = cleanPathname
    .replace(/^\//, "")
    .split("/")
    .filter(Boolean);
  const pageTitle = segments.map(humanize).join(" - ") || "Home";
  const title = friendlyTab
    ? `${pageTitle} - ${humanize(friendlyTab)}`
    : pageTitle;

  return { url, title };
}

// Umami's /api/send creates the session row only for "event" payloads; an
// "identify" that reaches it before that session exists is rejected with a 500
// (STIC-15669).
//
// Gating identify behind the first event *request* was not enough (STIC-15886)
// for two reasons:
//  - the gate resolved even when that event had failed, because the error was
//    swallowed - so identify still ran with no session behind it;
//  - Umami derives the session from ip + user-agent + a rotating salt, so the
//    session it looks up can change mid-visit (network switch, salt rotation)
//    and a one-shot gate can never notice.
//
// So instead: hold the identify payload until an event has actually been
// accepted, and re-arm it whenever an identify is rejected so that a later
// accepted event retries it. The proxy answers 200 with {"accepted": bool} - an
// older backend that returns an empty body is treated as accepted.
const MAX_IDENTIFY_ATTEMPTS = 3;

interface UsageAnalyticsAck {
  accepted?: boolean;
}

let sessionPrimed = false;
let pendingIdentify: Record<string, unknown> | null = null;
let identifyAttempts = 0;

/** Wait for the next accepted event before trying this identify again. */
function rearmIdentify(data: Record<string, unknown>): void {
  sessionPrimed = false;
  pendingIdentify ??= data;
}

function flushIdentify(): void {
  if (!sessionPrimed || !pendingIdentify) return;
  if (identifyAttempts >= MAX_IDENTIFY_ATTEMPTS) return;

  const data = pendingIdentify;
  pendingIdentify = null;
  identifyAttempts += 1;

  apiClient
    .post<UsageAnalyticsAck>("/rest/usage_analytics/identify", {
      hostname: window.location.hostname,
      data,
    })
    .then((response) => {
      if (response.data?.accepted === false) rearmIdentify(data);
    })
    .catch(() => rearmIdentify(data));
}

function postEvent(payload: Record<string, unknown>): void {
  apiClient
    .post<UsageAnalyticsAck>("/rest/usage_analytics/event", payload)
    .then((response) => {
      // An accepted event means Umami now holds a session for this visitor.
      if (response.data?.accepted === false) return;
      sessionPrimed = true;
      flushIdentify();
    })
    .catch(() => {});
}

const usageAnalyticsClient = {
  trackPageView(url: string, title: string): void {
    postEvent({
      hostname: window.location.hostname,
      screen: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      referrer: document.referrer,
      url,
      title,
    });
  },

  trackCustomEvent(name: string, data?: Record<string, unknown>): void {
    postEvent({
      hostname: window.location.hostname,
      screen: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      ...derivePageInfo(),
      name,
      data,
    });
  },

  identify(data: Record<string, unknown>): void {
    pendingIdentify = data;
    identifyAttempts = 0;
    flushIdentify();
  },
};

export default usageAnalyticsClient;
